/**
 * React Router v4 → v7 Compatibility Layer
 * 
 * This module provides compatibility wrappers to ease migration from 
 * React Router v4 to v7. It handles the key API differences:
 * - `component` prop → `element` prop
 * - `render` prop → `element` prop with inline JSX
 * - Automatic route props (match, location, history) → hooks
 * - history.block() → custom blocking implementation
 * 
 * Usage: Replace existing Route components with CompatRoute
 * After migration is complete, refactor to native v7 Routes.
 */
import React, { createContext, useContext, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Route, useParams, useLocation, useNavigate } from "react-router-dom";

// Context to share blocking functionality with class components
const BlockerContext = createContext(null);

/**
 * BlockerProvider - Provides navigation blocking functionality to child components
 * Wrap your router content with this to enable history.block() compatibility
 * 
 * Note: This implementation uses a custom blocking approach since useBlocker 
 * requires a data router. It intercepts navigate calls and shows confirmation.
 */
export function BlockerProvider({ children }) {
  const blockerCallbackRef = useRef(null);
  const locationRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Keep location ref updated
  locationRef.current = location;

  // Create a wrapped navigate function that checks with the blocker first
  const blockedNavigate = useCallback((...args) => {
    if (blockerCallbackRef.current) {
      // Determine the target location
      let targetPath;
      if (typeof args[0] === 'number') {
        // For go(-1), go(1), etc. - just allow, we can't easily block these
        return navigate(...args);
      } else if (typeof args[0] === 'string') {
        targetPath = args[0];
      } else if (args[0]?.pathname) {
        targetPath = args[0].pathname;
      }
      
      // Check if we should block
      if (targetPath && targetPath !== locationRef.current?.pathname) {
        const message = blockerCallbackRef.current({ pathname: targetPath });
        if (message) {
          if (window.confirm(message)) {
            return navigate(...args);
          }
          return; // Blocked - don't navigate
        }
      }
    }
    return navigate(...args);
  }, [navigate]);

  const block = useCallback((callback) => {
    blockerCallbackRef.current = callback;
    // Return unblock function
    return () => {
      blockerCallbackRef.current = null;
    };
  }, []);

  return (
    <BlockerContext.Provider value={{ block, navigate: blockedNavigate }}>
      {children}
    </BlockerContext.Provider>
  );
}

BlockerProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * useBlockerContext - Hook to access blocker functionality
 */
export function useBlockerContext() {
  return useContext(BlockerContext);
}

/**
 * ComponentWrapper - Wraps a v4-style component to provide router props via hooks
 */
function ComponentWrapper({ component: Component, ...rest }) {
  const params = useParams();
  const location = useLocation();
  const rawNavigate = useNavigate();
  const blockerContext = useBlockerContext();
  
  // Use blocked navigate if available, otherwise raw navigate
  const navigate = blockerContext?.navigate || rawNavigate;
  
  const history = {
    push: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { state: locationState });
      } else {
        navigate(pathOrLocation, { state });
      }
    },
    replace: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { replace: true, state: locationState });
      } else {
        navigate(pathOrLocation, { replace: true, state });
      }
    },
    goBack: () => rawNavigate(-1),
    goForward: () => rawNavigate(1),
    block: blockerContext?.block || (() => () => {}),
    location,
  };
  
  const match = {
    params,
    path: location.pathname,
    url: location.pathname,
  };
  
  return <Component {...rest} match={match} location={location} history={history} navigate={navigate} />;
}

ComponentWrapper.propTypes = {
  component: PropTypes.elementType,
};

/**
 * RenderWrapper - Wraps a v4-style render prop to provide router props via hooks
 */
function RenderWrapper({ render, ...rest }) {
  const params = useParams();
  const location = useLocation();
  const rawNavigate = useNavigate();
  const blockerContext = useBlockerContext();
  
  // Use blocked navigate if available, otherwise raw navigate
  const navigate = blockerContext?.navigate || rawNavigate;
  
  const history = {
    push: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { state: locationState });
      } else {
        navigate(pathOrLocation, { state });
      }
    },
    replace: (pathOrLocation, state) => {
      if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
        const { pathname, search, hash, state: locationState } = pathOrLocation;
        const path = pathname + (search || '') + (hash || '');
        navigate(path, { replace: true, state: locationState });
      } else {
        navigate(pathOrLocation, { replace: true, state });
      }
    },
    goBack: () => rawNavigate(-1),
    goForward: () => rawNavigate(1),
    block: blockerContext?.block || (() => () => {}),
    location,
  };
  
  const match = {
    params,
    path: location.pathname,
    url: location.pathname,
  };
  
  return render({ ...rest, match, location, history, navigate });
}

/**
 * convertRouteToV7 - Converts a v4 Route element to v7 format
 * Used by convertRoutesToV7 to process route arrays
 */
export function convertRouteToV7(v4Route, index) {
  if (!v4Route || !v4Route.props) return null;
  
  const { path, exact, component: Component, render, children, ...otherProps } = v4Route.props;
  const key = v4Route.key || `route-${index}`;
  
  // In v7, all routes are exact by default. Use path="something/*" for non-exact matching
  let v7Path = path;
  if (path && !exact && path !== "/" && !path.endsWith("*")) {
    v7Path = `${path}/*`;
  }
  
  let element;
  if (Component) {
    element = <ComponentWrapper component={Component} {...otherProps} />;
  } else if (render) {
    element = <RenderWrapper render={render} {...otherProps} />;
  } else if (children) {
    element = children;
  }
  
  // Preserve original props (like topMenuTitle, rootCssClass) on the Route element
  // so CoreLayout can access them for header display
  return <Route key={key} path={v7Path || "*"} element={element} {...otherProps} />;
}

/**
 * convertRoutesToV7 - Converts an array of v4 Route elements to v7 format
 * Use this when you have an array of routes from AuthorizedRoutes
 */
export function convertRoutesToV7(v4Routes) {
  if (!Array.isArray(v4Routes)) {
    return convertRouteToV7(v4Routes, 0);
  }
  return v4Routes.map((route, index) => convertRouteToV7(route, index));
}

export default { convertRouteToV7, convertRoutesToV7, BlockerProvider, useBlockerContext };
